import moment, { Moment } from 'moment';
import { useState } from 'react';
import { UtilHelper } from '../../../../../../shared/helpers/utility';
import { useSelector } from 'react-redux';
import { GENERAL_STATE_KEY, generalStateSelector } from '../../../../../../redux/general/general-reducer';
import { FOODMENU_INLINE_ERROR } from '../../../redux/foodmenu-constant';
import { MenuDiscount } from '../../../../../../shared/models/food-menu';
import { TIME_24 } from '../../../../../../shared/helpers/constant';

interface TimeList {
	value: string;
	disabled: boolean;
}

const nextAvailableStartPromotionDateTime = () => {
	let result = moment();
	const minute = result.get('minute');
	if (minute + NextAvailableDiscountTimeGap >= 60) {
		result = result.set('minute', 30).add(1, 'hour')
	} else if (minute + NextAvailableDiscountTimeGap >= 30) {
		result = result.set('minute', 0).add(1, 'hour');
	} else {
		result = result.set('minute', 30);
	}

	return result;
};

const nextAvailableHour = () => {
	return nextAvailableStartPromotionDateTime().format('HH:mm');
}

export const NextAvailableDiscountTimeGap = 5;

export const useMenuDiscount = () => {
	const now = moment();
	const [startPromotionDateTime, setStartPromotionDateTime] =
		useState<{date: Moment | null, time: string}>({date: null, time: ''});
	const [discountObject, setDiscountObject] = useState({discountedPrice: '', isChanged: false});
	const [endPromotionDateTime, setEndPromotionDateTime] =
		useState<{date: Moment | null, time: string}>({date: null, time: ''});
	const appConfig = useSelector(generalStateSelector(GENERAL_STATE_KEY.REMOTE_CONFIG))
	const [startTimeList, setStartTimeList] = useState<TimeList[]>(TIME_24.map(value => ({value, disabled: false})))
	const [endTimeList, setEndTimeList] = useState<TimeList[]>(TIME_24.map(value => ({value, disabled: false})))

	const isDateTimeValid = (date: Moment | null, time: string) => {
		return date?.isValid() && time !== '';
	}

	const toMoment = (date: Moment, time: string) => {
		if (!time) {
			time = '00:00';
		}
		return moment(date.format('YYYY-MM-DD') + time, 'YYYY-MM-DDHH:mm')
	}

	const isAfterDateBeforeStartDate = (startDateTime: Moment, endDateTime: Moment, gapMinute = 0) => {
		return startDateTime.clone().add(gapMinute, 'minute').isAfter(endDateTime)
	}

	const updateStartPromotionDate = (input: Moment) => {
		if (!input?.isValid()) {
			setStartPromotionDateTime(prev => ({...prev, date: null}));
			return;
		}

		setStartPromotionDateTime(prev => ({...prev, date: input.startOf('date')}))

		if (
			!isDateTimeValid(input, startPromotionDateTime.time) ||
			!isDateTimeValid(endPromotionDateTime.date, endPromotionDateTime.time)
		) {
			return;
		}

		const isDatesInvalid = isAfterDateBeforeStartDate(
			toMoment(input, startPromotionDateTime.time),
			toMoment(endPromotionDateTime.date as Moment, endPromotionDateTime.time),
			NextAvailableDiscountTimeGap
		)

		if (isDatesInvalid) {
			return [
			{
				name: 'endPromotionDate',
				errors: [FOODMENU_INLINE_ERROR.DISCOUNT_DURATION_INVALID]
			},
			{
				name: 'endPromotionTime',
				errors: ['']
			}
		]
		}
	};

	const updateStartPromotionTime = (value: string) => {
		setStartPromotionDateTime(prev => ({...prev, time: value}));
		if (
			!isDateTimeValid(startPromotionDateTime.date, value) ||
			!isDateTimeValid(endPromotionDateTime.date, endPromotionDateTime.time)
		) {
			return;
		}
		const isDateInvalid = isAfterDateBeforeStartDate(
			toMoment(startPromotionDateTime.date as Moment, value),
			toMoment(endPromotionDateTime.date as Moment, endPromotionDateTime.time),
			NextAvailableDiscountTimeGap
		)

		if (!isDateInvalid) return;

		return [
			{
				name: 'endPromotionDate',
				errors: [FOODMENU_INLINE_ERROR.DISCOUNT_DURATION_INVALID]
			},
			{
				name: 'endPromotionTime',
				errors: ['']
			}
		]
	}

	const updateEndPromotionDate = (input: Moment) => {
		if (!input?.isValid()) {
			setEndPromotionDateTime(prev => ({...prev, date: null}));
			return;
		}
		input.startOf('date');
		setEndPromotionDateTime(prev => ({...prev, date: input}));

		if (!startPromotionDateTime.date?.isValid() || !startPromotionDateTime.time || !endPromotionDateTime.time) {
			return;
		}
		const isDateInvalid = isAfterDateBeforeStartDate(
			toMoment(startPromotionDateTime.date, startPromotionDateTime.time),
			toMoment(input, endPromotionDateTime.time),
			NextAvailableDiscountTimeGap
		)

		if (!isDateInvalid) return;

		return [
			{
				name: 'endPromotionDate',
				errors: [FOODMENU_INLINE_ERROR.DISCOUNT_DURATION_INVALID]
			},
			{
				name: 'endPromotionTime',
				errors: ['']
			}
		]
	}

	const updateEndPromotionTime = (value: string) => {
		setEndPromotionDateTime(prev => ({...prev, time: value}));
		if (!startPromotionDateTime.date?.isValid() || !startPromotionDateTime.time || !endPromotionDateTime.date?.isValid()) {
			return;
		}

		const isDateInvalid = isAfterDateBeforeStartDate(
			toMoment(startPromotionDateTime.date, startPromotionDateTime.time),
			toMoment(endPromotionDateTime.date, value)
		)

		if (!isDateInvalid) return;

		return [
			{
				name: 'endPromotionDate',
				errors: [FOODMENU_INLINE_ERROR.DISCOUNT_DURATION_INVALID]
			},
			{
				name: 'endPromotionTime',
				errors: ['']
			}
		]
	}

	const updateDiscountedPrice = (value: string) => {
		setDiscountObject({
			discountedPrice: value,
			isChanged: true
		});
	}

	const setInitialValues = (menuDiscount: MenuDiscount) => {
		const priceDiscount = String(menuDiscount.priceDiscount)
		setDiscountObject({
			isChanged: true,
			discountedPrice: priceDiscount
		})

		const startDateTime = moment(menuDiscount.startDate)
		const [startDate, startTime] = [startDateTime.clone().startOf('date'), startDateTime.format('HH:mm')]
		setStartPromotionDateTime({
			date: startDate,
			time: startTime
		})

		let endDateTime: {date: Moment | null, time: string} = {date: null, time: ''}
		const isEnableEndDate = !!menuDiscount.endDate;
		if (isEnableEndDate) {
			const endDateTimeMoment = moment(menuDiscount.endDate)
			const [endDate, endTime] = [endDateTimeMoment.clone().startOf('date'), endDateTimeMoment.format('HH:mm')]
			setEndPromotionDateTime({
				date: endDate,
				time: endTime
			})
			endDateTime = { date: endDate, time: endTime}
		}

		return {
			priceDiscount,
			startDateTime: { startDate, startTime},
			endDateTime: {
				endDate: endDateTime.date,
				endTime: endDateTime.time
			},
			isEnableEndDate,
		}

	}

	const shouldDisableStartDate = (current: Moment) => {
		return current && !current.endOf('day').isAfter(now);
	};

	const shouldDisableEndDate = (current: Moment) => {
		if (!startPromotionDateTime.date?.isValid()) {
			return shouldDisableStartDate(current);
		}

		return (current
			&& current.startOf('date').isBefore(startPromotionDateTime.date.clone().startOf('date')))
			|| current.startOf('date').isBefore(now, 'date');
	}

	const shouldDisableStartTimeOption = (value: string) => {
		if (!startPromotionDateTime.date?.isValid()) return false;
		const startDateTime = toMoment(startPromotionDateTime.date, startPromotionDateTime.time);

		if (startDateTime.isAfter(now, 'date')) {
			return false;
		}

		const inputDateTime = toMoment(startPromotionDateTime.date, value);
		if (!inputDateTime.isValid()) {
			return false;
		}

		return isAfterDateBeforeStartDate(now, inputDateTime, NextAvailableDiscountTimeGap)
	};

	const shouldDisableEndTimeOption = (value: string) => {
		if (!startPromotionDateTime.date?.isValid() || !endPromotionDateTime.date?.isValid()) return false;

		const endDateTime = toMoment(endPromotionDateTime.date, value)
		if (isAfterDateBeforeStartDate(now, endDateTime, NextAvailableDiscountTimeGap)) {
			return true;
		} else if (startPromotionDateTime.date.isBefore(endPromotionDateTime.date, 'date')) {
			return false;
		}

		const [startHour, startMinute] = startPromotionDateTime.time.split(':').map(Number);
		const [endHour, endMinute] = value.split(':').map(Number);
		return startHour > endHour || (startHour === endHour && startMinute >= endMinute);
	}

	const validateDiscountedPrice = (normalPrice: number) => {
		const {discountedPrice, isChanged} = discountObject;
		if (!isChanged) {
			return Promise.resolve();
		}

		const value = UtilHelper.removeComma(discountedPrice)
		
		if (isNaN(value)) {
			return Promise.resolve();
		} else if (Number(value) >= normalPrice) {
			return Promise.reject(FOODMENU_INLINE_ERROR.DISCOUNT_PRICE_LESS_THAN_NORMAL_PRICE)
		}
		const percent = Math.floor(((normalPrice - value) / normalPrice) * 100)
		const maxPercent = getMaximumDiscountPercent();
		if (percent > maxPercent) {
			return Promise.reject(FOODMENU_INLINE_ERROR.DISCOUNT_PRICE_INVALID.replace('%s', String(maxPercent)));
		}

		return Promise.resolve();
	}

	const getMaximumDiscountPercent = () => {
		let maxPercent = 70;
		if (Array.isArray(appConfig)) {
			const maxPercentConfig = appConfig.find(item => item.key === 'merchant.menu.discount.maxpercent')?.value
			if (!isNaN(Number(maxPercentConfig))) {
				maxPercent = Number(maxPercentConfig)
			}
		}

		return maxPercent;
	}

	const resetFields = () => {
		setStartPromotionDateTime({date: moment().startOf('date'), time: nextAvailableHour()})
		setDiscountObject({discountedPrice: '', isChanged: false})
		setEndPromotionDateTime({date: null, time: ''})
	}

	const displayStartTimeOption = () => {
		const items = TIME_24.map(value => ({value, disabled: shouldDisableStartTimeOption(value)}))
		setStartTimeList(items)
	}

	const displayEndTimeOption = () => {
		const items = TIME_24.map(value => ({value, disabled: shouldDisableEndTimeOption(value)}))
		setEndTimeList(items)
	}

	return {
		discountedPrice: discountObject.discountedPrice,
		isDiscountedPriceChange: discountObject.isChanged,
		startDate: startPromotionDateTime.date,
		startTime: startPromotionDateTime.time,
		endDate: endPromotionDateTime.date,
		endTime: endPromotionDateTime.time,
		endPromotionDateTime,
		startTimeList,
		endTimeList,
		getMaximumDiscountPercent,
		validateDiscountedPrice,
		updateStartPromotionDate,
		updateStartPromotionTime,
		updateEndPromotionDate,
		updateEndPromotionTime,
		updateDiscountedPrice,
		setInitialValues,
		displayStartTimeOption,
		shouldDisableStartDate,
		shouldDisableEndDate,
		displayEndTimeOption,
		resetFields,
		toMoment,
		isAfterDateBeforeStartDate
	};
};
